import React, {useEffect, useState} from 'react';
import {Form, Input, Select} from 'antd';
import { passwordPolicyRule } from 'utils/passwordValidator';
import EditableDrawer from 'components/EditableDrawer';
import {FormInstance} from 'antd/es/form';
import {CountryCodeAndPhoneNumber, PhoneInput} from 'components/PhoneInput';
import {SiteType} from 'graphql/types/SiteType';
import {ROLE_OPTIONS} from 'constants/roleOptions';
import {RoleType} from 'graphql/types/AccountType';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { AccountFormData } from 'hooks/useAccountApis';
import { useTranslation } from 'react-i18next';
import { useDspsByHubIdForWms } from '../../../../graphql/queries/getDspsByHubIdForWms';
import { getAccountDspManagerByAccountId } from '../../../central/account/graphql/queries/getAccountDspManagerByAccountId';
import { showError } from 'utils/exceptionUtils';

interface AccountFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: AccountFormData) => Promise<void>;
    account?: AccountFormData | null; // If account data is provided, it's edit mode, otherwise it's create mode
    mode: 'create' | 'edit'; // Explicitly specify mode
}

const AccountFormDrawer: React.FC<AccountFormDrawerProps> = ({
                                                                 visible,
                                                                 onClose,
                                                                 onSubmit,
                                                                 account,
                                                                 mode
                                                             }) => {
    const { t } = useTranslation();

    const drawerTitle = mode === 'create' ? t('wms.account.pageDetails.newPersonnel') : t('wms.account.pageDetails.personnelDetails');
    const [form] = Form.useForm();
    const { currentWarehouse: warehouse } = useWarehouseContext();

    useEffect(() => {
        const getDefaultRole = () => {
            if (warehouse?.siteType === SiteType.WAREHOUSE) {
                return 'WAREHOUSE_OPERATOR';
            } else if (warehouse?.siteType === SiteType.HUB) {
                return 'HUB_OPERATOR';
            }
            return undefined;
        }

        const initializeForm = async () => {
            try {
                if (account) {
                    // 编辑模式：使用 account 中的角色，不要覆盖
                    form.resetFields();
                    form.setFieldsValue({
                        ...account,
                        role: account.role, // 保持原有角色
                        warehouseId: warehouse?.id,
                    });
                    
                    // 如果是 DSP_MANAGER 或 DSP_OWNER_TEAM 角色，需要获取 dspId
                    if (account.role === 'DSP_MANAGER' || account.role === 'DSP_OWNER_TEAM') {
                        try {
                            const dspManager = await getAccountDspManagerByAccountId(account.id!);
                            form.setFieldsValue({
                                dspId: dspManager?.dspId || undefined
                            });
                        } catch (error) {
                            console.error('Error fetching DSP manager data:', error);
                            showError(error);
                            // 即使获取 dspId 失败，也不应该阻止表单显示
                        }
                    }
                } else {
                    // 创建模式：不设置默认角色，让用户自己选择
                    form.resetFields();
                    form.setFieldsValue({
                        status: 1,
                        role: undefined, // 不设置默认角色
                        warehouseId: warehouse?.id,
                        phone: {
                            countryCode: 1,
                            phoneNumber: '',
                        },
                    });
                }
            } catch (error) {
                console.error('Error initializing form:', error);
                showError(error);
                // 即使初始化失败，也不应该阻止 drawer 显示
            }
        };

        if (visible) {
            initializeForm();
        }
    }, [account, form, visible, warehouse]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();
            
            // 将时间范围转换为独立的开始和结束时间
            const processedValues = {
                ...values,
            };
            
            await onSubmit(processedValues);
        } catch (error) {
            console.error('error', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmText={isSubmitting ? t('wms.account.messages.processing') as string : t('wms.account.messages.confirm') as string}
            cancelText={t('wms.account.messages.cancel') as string}
            confirmDisabled={isSubmitting}
            width={600}
        >
            <AccountForm
                form={form}
                mode={mode}
            />
        </EditableDrawer>
    );
};

interface AccountFormProps {
    mode: 'create' | 'edit';
    form: FormInstance<AccountFormData>;
}

const AccountForm = ({mode, form}: AccountFormProps) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const role = Form.useWatch('role', form);
    const hubId = currentWarehouse?.siteType === SiteType.HUB ? Number(currentWarehouse.id) : undefined;
    const { dsps, loading: dspsLoading } = useDspsByHubIdForWms(hubId);

    const STATUS_OPTIONS = [
        {value: 1, label: t('enum.activeStatus.active')},
        {value: 0, label: t('enum.activeStatus.suspended')}
    ];

    // 根据 warehouse 类型确定可用的角色选项（排除 'all' 选项，因为这是筛选用的）
    const getAvailableRoles = () => {
        if (currentWarehouse?.siteType === SiteType.HUB) {
            // Hub 可以选择 HUB_OPERATOR、PICKUP_STAFF、DSP_MANAGER 或 DSP_OWNER_TEAM
            const hubRoles: Array<RoleType> = ['HUB_OPERATOR', 'SORTING_STAFF', 'SORTER_OPERATOR', 'RETURNS_STAFF', 'PICKUP_STAFF', 'DSP_MANAGER', 'DSP_OWNER_TEAM'];
            return ROLE_OPTIONS.filter(option =>
                hubRoles.includes(option.value as RoleType)
            );
        } else if (currentWarehouse?.siteType === SiteType.WAREHOUSE) {
            // Warehouse 可以选择 WAREHOUSE_OPERATOR 或 WAREHOUSE_TAG_SORTER（仓库贴码分拣员）
            const warehouseRoles: Array<RoleType> = ['WAREHOUSE_OPERATOR', 'WAREHOUSE_TAG_SORTER'];
            return ROLE_OPTIONS.filter(option =>
                warehouseRoles.includes(option.value as RoleType)
            );
        }
        return ROLE_OPTIONS.filter(option => option.value !== 'all');
    };

    const availableRoleOptions = getAvailableRoles().map(option => ({
        value: option.value,
        label: t(`enum.role.${option.value}`)
    }));

    const onFinish = (values: any) => {
        console.log(values);
    }

    const WarehouseOperatorOptions = () => {
        return (
            <>
                <Form.Item label={t('wms.account.pageDetails.warehouse')} name="warehouseId"
                           rules={[{required: true, message: t('wms.account.messages.pleaseSelectWarehouse') as string}]}>
                    <Select
                        disabled={true}
                        options={currentWarehouse ? [{value: currentWarehouse.id, label: currentWarehouse.name}] : []}
                        placeholder={t('wms.account.pageDetails.select')}
                    />
                </Form.Item>
            </>
        )
    };

    const HubOperatorOptions = () => {
        return (
            <>
                <Form.Item label={t('wms.account.pageDetails.hub')} name="warehouseId" rules={[{required: true, message: t('wms.account.messages.pleaseSelectHub') as string}]}>
                    <Select
                        disabled={true}
                        options={currentWarehouse ? [{value: currentWarehouse.id, label: currentWarehouse.name}] : []}
                        placeholder={t('wms.account.pageDetails.select')}
                    />
                </Form.Item>
            </>
        )
    };

    const DspManagerOptions = () => {
        const dspOptions = dsps.map(dsp => ({ value: dsp.id, label: dsp.name }));
        return (
            <>
                <Form.Item 
                    label={t('central.personnel.pageDetails.company') || t('wms.account.pageDetails.company')} 
                    name="dspId" 
                    rules={[{required: true, message: t('central.personnel.messages.pleaseSelectCompany') || t('wms.account.messages.pleaseSelectCompany') as string}]}
                >
                    <Select
                        loading={dspsLoading}
                        options={dspOptions}
                        placeholder={t('components.input.select') as string}
                        disabled={dspsLoading || dspOptions.length === 0}
                    />
                </Form.Item>
            </>
        )
    };

    return (
        <Form
            form={form}
            onFinish={onFinish}
            labelAlign="left"
            labelCol={{ span: 6 }}
            wrapperCol={{span: 18}}
        >
            {mode === 'edit' && (
                <Form.Item label={t('wms.account.pageDetails.id')} name="id">
                    <Input placeholder={t('wms.account.pageDetails.inputPlaceholder') as string} disabled/>
                </Form.Item>
            )}
            <Form.Item label={t('wms.account.pageDetails.phone')}
                       name="phone"
                       rules={[
                           {
                               validator: (_, value: CountryCodeAndPhoneNumber) => {
                                   if (!value || !value.phoneNumber) {
                                       return Promise.reject(new Error(t('wms.account.messages.phoneNumberRequired') as string));
                                   }

                                   // 验证手机号只包含数字
                                   const phoneRegex = /^\d+$/;
                                   if (!phoneRegex.test(value.phoneNumber)) {
                                       return Promise.reject(new Error(t('wms.account.messages.phoneNumberDigitsOnly') as string));
                                   }

                                   return Promise.resolve();
                               }
                           }
                       ]}
            >
                <PhoneInput/>
            </Form.Item>
            <Form.Item
                label={t('wms.account.pageDetails.password')}
                name="password"
                validateTrigger="onBlur"
                rules={mode === 'create'
                    ? [{required: true, message: t('wms.account.messages.pleaseInputPassword') as string}, passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
                    : [passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]}
            >
                <Input.Password placeholder={t('accountSecurity.passwordHint') as string}/>
            </Form.Item>
            <Form.Item
                label={t('wms.account.pageDetails.name')}
                name="name"
                rules={[{required: true, message: t('wms.account.messages.pleaseInputName') as string}]}
            >
                <Input placeholder={t('wms.account.pageDetails.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item
                label={t('wms.account.pageDetails.email')}
                name="email"
                rules={[{type: 'email', message: t('wms.account.messages.pleaseInputValidEmail') as string}]}
            >
                <Input placeholder={t('wms.account.pageDetails.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item label={t('wms.account.pageDetails.personnelStatus')} name="status">
                <Select
                    options={STATUS_OPTIONS}
                    placeholder={t('wms.account.pageDetails.select')}
                />
            </Form.Item>
            <Form.Item
                label={t('wms.account.pageDetails.role')}
                name="role"
                rules={[{required: true, message: t('wms.account.messages.pleaseSelectRole') as string}]}
            >
                <Select
                    disabled={mode === 'edit'}
                    options={availableRoleOptions}
                    placeholder={t('wms.account.pageDetails.select')}
                />
            </Form.Item>
            {(role === 'HUB_OPERATOR' || role === 'SORTING_STAFF' || role === 'SORTER_OPERATOR' || role === 'RETURNS_STAFF' || role === 'PICKUP_STAFF') && <HubOperatorOptions />}
            {(role === 'WAREHOUSE_OPERATOR' || role === 'WAREHOUSE_TAG_SORTER') && <WarehouseOperatorOptions />}
            {(role === 'DSP_MANAGER' || role === 'DSP_OWNER_TEAM') && <DspManagerOptions />}
          </Form>
    )
};

export default AccountFormDrawer; 